import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import SeoHead from '@/components/base/SeoHead';
import ArticleCard from '@/components/base/ArticleCard';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-screen flex items-center justify-center bg-background-50">
          <div className="text-center">
            <div className="w-20 h-20 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-6">
              <i className="ri-file-search-line text-3xl text-foreground-400"></i>
            </div>
            <h2 className="text-xl font-semibold text-foreground-950 mb-2">文章未找到</h2>
            <p className="text-foreground-500 mb-6">您访问的文章不存在或已被移除</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 rounded-lg font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-home-4-line"></i> 返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  const categoryName =
    article.category === 'guide' ? '平台指南' : article.category === 'news' ? '最新动态' : '帮助中心';

  const articleSchema = [
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, item: { '@id': 'https://8866h.cc', name: '首页' } },
        { '@type': 'ListItem', position: 2, item: { '@id': `https://8866h.cc/category/${article.category}`, name: categoryName } },
        { '@type': 'ListItem', position: 3, item: { '@id': `https://8866h.cc/article/${article.slug}`, name: article.title } },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.title,
      description: article.excerpt,
      author: {
        '@type': 'Organization',
        name: article.author,
      },
      datePublished: article.publishDate,
      image: article.thumbnail,
      publisher: {
        '@type': 'Organization',
        name: '8866h.cc',
      },
    },
  ];

  return (
    <>
      <SeoHead
        title={`${article.title} | 8866h官网`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://8866h.cc/article/${article.slug}`}
        ogType="article"
        ogImage={article.thumbnail}
        schemaData={articleSchema}
        isArticle
        articlePublishedTime={article.publishDate}
        articleModifiedTime={article.publishDate}
      />

      <Navbar />

      <main className="pt-20">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <Breadcrumbs
              items={[
                { label: '首页', path: '/' },
                { label: categoryName, path: `/category/${article.category}` },
                { label: article.title },
              ]}
            />
          </div>
        </div>

        <article className="py-10 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            {/* Header */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <Link
                  to={`/category/${article.category}`}
                  className="text-xs font-medium text-primary-600 bg-primary-100 px-3 py-1.5 rounded-full hover:bg-primary-200 transition-colors whitespace-nowrap cursor-pointer"
                >
                  {categoryName}
                </Link>
                <span className="text-xs text-foreground-400">{article.publishDate}</span>
                <span className="text-xs text-foreground-400">阅读 {article.readTime}</span>
                <span className="text-xs text-foreground-400">作者：{article.author}</span>
              </div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4 font-heading leading-tight">
                {article.title}
              </h1>
              <p className="text-base text-foreground-500 leading-relaxed">{article.excerpt}</p>
            </div>

            {/* Featured Image */}
            <div className="rounded-xl overflow-hidden mb-10">
              <img
                src={article.thumbnail}
                alt={article.title}
                title={`${article.title} - 8866h.cc`}
                className="w-full aspect-[16/9] object-cover"
              />
            </div>

            {/* Content */}
            <div
              className="prose prose-lg max-w-none mb-12"
              dangerouslySetInnerHTML={{
                __html: article.content
                  .replace(/<h2/g, '<h2 class="text-xl md:text-2xl font-bold text-foreground-950 mt-10 mb-4 font-heading"')
                  .replace(/<h3/g, '<h3 class="text-lg md:text-xl font-semibold text-foreground-950 mt-8 mb-3"')
                  .replace(/<p>/g, '<p class="text-foreground-700 leading-relaxed mb-4">')
                  .replace(/<ul>/g, '<ul class="list-disc pl-6 mb-4 space-y-2">')
                  .replace(/<li>/g, '<li class="text-foreground-700 leading-relaxed">')
                  .replace(/<strong>/g, '<strong class="font-semibold text-foreground-900">'),
              }}
            />

            {/* Tags */}
            <div className="flex items-center gap-2 flex-wrap pt-6 border-t border-background-200/70">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="text-xs text-foreground-500 bg-background-100 px-3 py-1.5 rounded-full"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </div>
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="py-16 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-8 font-heading">相关文章</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {relatedArticles.map((ra) => (
                  <ArticleCard key={ra.slug} article={ra} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}